import jsPDF from "jspdf";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";

import uRLs from "../../utils/baseUtils";
import { usePermission } from "../../context/PermissionContext";

import {
  capitalizeFirstLetter,
  loadImageDimensions,
  parseAmount,
} from "../modules/invoiceUtils";

export const generateIN_HA4_1PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const {
    documentConfig,
    isInterState,
    isPreSale,
    timezone,    
  } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const padding = 5;
  const headerX = 2;
  const headerY = 2;
  const HALF_PAGE_HEIGHT = 140;

  const imagePath = documentConfig?.image
  if (documentConfig?.image) {
    try {
      // Convert image URL to base64
      const response = await fetch(imagePath);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 30;
      const maxHeight = 8;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      // Determine format
      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(imagePath);

      // Add image to PDF
      doc.addImage(base64, format, padding, padding, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }

  const invoiceFontSize = 9;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(invoiceFontSize);
  // Estimate vertical alignment by adjusting for font size
  const textHeightOffset = invoiceFontSize * 0.55;
  const invoiceTextY = headerY + textHeightOffset;
  const invoiceX = pageWidth - padding;
  const invoiceText = documentConfig?.title || "";
  const invoiceTextWidth = doc.getTextWidth(invoiceText);
  doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
  // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
  if (data?.status === "CANCELLED") {
    const cancelledFontSize = invoiceFontSize * 0.6;
    doc.setFontSize(cancelledFontSize);
    doc.setTextColor(220, 53, 69); // Bootstrap danger red
    doc.setFont("Inter-Bold", "bold");
    // Position CANCELLED right after INVOICE
    const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
    doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
    // Reset styles
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(invoiceFontSize);
  }

  // Prepare font size for labels and values
  doc.setFontSize(7);
  const invoiceNoLabel = documentConfig?.numberLabel;
  const invoiceNoValue = documentConfig?.number;
  const invoiceDateLabel = "Date:";
  const saleDate = data?.saledate || data?.preSaleData?.preSaleUTCDate;
  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const invoiceDateValue = documentConfig?.Date?.date // Measure total widths
  const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
  const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
  const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
  const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
  const gap = 5;
  const innerGap = 2;
  const invoiceRowY = 15;
  const totalTextWidth =
    invoiceNoLabelWidth +
    innerGap +
    invoiceNoValueWidth +
    gap +
    invoiceDateLabelWidth +
    innerGap +
    invoiceDateValueWidth;
  const invoiceRowX = pageWidth - padding - totalTextWidth;
  const lineStartX = padding;
  const lineEndX = invoiceRowX - 5;
  const lineY = invoiceRowY;
  // Middle line
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  doc.setLineWidth(2);
  doc.setLineCap("round");
  doc.line(lineStartX, lineY, lineEndX, lineY);

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceNoValue,
    invoiceRowX + invoiceNoLabelWidth + innerGap,
    invoiceRowY,
  );
  const dateX =
    invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceDateLabel, dateX, invoiceRowY);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceDateValue,
    dateX + invoiceDateLabelWidth + innerGap,
    invoiceRowY,
  );
  // --- Invoice From (between logo & title, in 2 lines) ---
  const topY = headerY + padding; // logo top
  const bottomY = invoiceTextY; // invoice title baseline
  const middleY = topY + (bottomY - topY) / 2; // midpoint

  // Line 1: Company + Address
  const line1 = [
    documentConfig?.fromData?.title || "-",
    [
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      //  documentConfig?.fromData?.country,
      documentConfig?.fromData?.pincode
        ? `- ${documentConfig?.fromData.pincode}`
        : "",
         documentConfig?.fromData?.phone
      ? `Phone: ${documentConfig.fromData.phone}${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`
      : documentConfig?.fromData?.additionalPhone
        ? `Phone: ${documentConfig.fromData.additionalPhone}`
        : "",
      documentConfig?.fromData?.email
        ? `Email: ${documentConfig?.fromData.email}`
        : "",
      documentConfig?.fromData?.gstnumber
        ? `GST: ${documentConfig?.fromData.gstnumber}`
        : "",
    ]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  const fromDetails = [line1];

  const leftX1 = padding + 35;
  const rightX = pageWidth - padding - invoiceTextWidth - 10;
  const availableWidth = rightX - leftX1 - 5;
  let currentY = middleY;
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);
  fromDetails.forEach((line) => {
    const wrappedLine = doc.splitTextToSize(line, availableWidth);
    doc.text(wrappedLine, leftX1, currentY, { align: "left" });
    currentY += wrappedLine.length * 4;
  });

  let toY = invoiceRowY + 6;
  // --- Invoice To ---
  const hasSaleToData =
    documentConfig?.toData &&
    (documentConfig?.toData?.name ||
      documentConfig?.toData?.addressline1 ||
      documentConfig?.toData?.addressline2 ||
      documentConfig?.toData?.district ||
      documentConfig?.toData?.country ||
      documentConfig?.toData?.pincode ||
      documentConfig?.toData?.phone ||
      documentConfig?.toData?.email ||
      documentConfig?.toData?.gstnumber ||
      documentConfig?.toData?.state ||
      documentConfig?.toData?.statecode ||
      documentConfig?.toData?.referenceid);

  if (hasSaleToData) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8.5);
    doc.text(documentConfig?.toLabel, padding, toY);

    toY += 3;

    const toDetailsLine = [
      documentConfig?.toData?.name || "-",
      [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.statecode,
        //  documentConfig?.toData?.country,
        documentConfig?.toData?.pincode
          ? `- ${documentConfig?.toData.pincode}`
          : "",
      ]
        .filter(Boolean)
        .join(", "),
      documentConfig?.toData?.phone
        ? `Phone: ${documentConfig?.toData.phone}`
        : "",
      documentConfig?.toData?.email
        ? `Email: ${documentConfig?.toData.email}`
        : "",
      documentConfig?.toData?.gstnumber
        ? `GST: ${documentConfig?.toData.gstnumber}`
        : "",
      documentConfig?.toData?.referenceid
        ? `Reference No: ${documentConfig?.toData.referenceid}`
        : "",
      documentConfig?.toData?.fssai
        ? `FASSI: ${documentConfig?.toData.fssai}`
        : "",
      documentConfig?.toData?.cinnumber
        ? ` CIN: ${documentConfig?.toData.cinnumber}`
        : "",
      documentConfig?.toData?.pannumber
        ? `PAN: ${documentConfig?.toData.pannumber}`
        : "",
    ]
      .filter(Boolean)
      .join(", ");

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    const wrappedAddress = doc.splitTextToSize(
      toDetailsLine,
      pageWidth - padding * 2,
    );

    // 2. Draw the wrapped text
    doc.text(wrappedAddress, padding, toY);

    // 3. IMPORTANT: Update toY based on the number of wrapped lines
    // This ensures the table starts BELOW the address
    toY += wrappedAddress.length * 3;
  }

  const productBody = documentConfig?.items?.map((item, i) => {
    const qty = item.quantity || 1;
    const rate = item.soldprice;
    const total = item.lineTotalSoldPrice;
    const gross = item.lineTotalSoldPrice;
    const taxable = item.lineTotalBasePrice;
    const gstAmt = item.lineTotalTaxAmount;
    const landingCost = item?.lineTotalLandingCost;
    const taxPercentage = item?.taxpercentage
    const desc = [
      `${item.title}`,
      ...(item.imode === "PRODUCT" && item.note ? [item.note] : []),
      ...(item.imode === "SERVICE" && item.description
        ? [item.description]
        : []),
      ...(item.hsnsac ? [`HSN/SAC : ${item.hsnsac}`] : []),
      ...(item.discount > 0 ? [`MRP     : ${item.mrp}`] : []),
      ...(item.discount > 0 ? [`Discount: - ${item.discount}`] : []),
      `GST     : ${item.taxpercentage}%`,
    ].join("\n");

    return [
      i + 1,
      desc,
      `${rate}`,
      `${qty} ${(() => {
        const unitCode = item?.isSecondaryUnit
          ? item?.unitConfig?.secondaryUnitCode
          : item?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      `${gross}`,
      `${taxable}`,
      `${taxPercentage}%`,
      `${gstAmt}`,
      `${total}`,
    ];
  });

  productBody.push([
    {
      content: "",
      colSpan: 2,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: "Total",
      styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      //  content: data.totalqty,
      content: "",
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },

    {
      content: `${documentConfig?.totalSoldPrice}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" ,fontSize:6},
    },
    {
      content: `${documentConfig?.totalBasePrice}`,
      styles: { fontStyle: "bold", font: "Inter-Bold",fontSize:6 },
    },
    {      
      content: "",
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: `${documentConfig?.totalTaxAmount}`,
      styles: { fontStyle: "bold", font: "Inter-Bold",fontSize:6 },
    },
    {
      content: `${documentConfig?.totalSoldPrice}`,
      styles: { fontStyle: "bold", font: "Inter-Bold",fontSize:6 },
    },
  ]);

  const commonTableWidth = doc.internal.pageSize.getWidth() - padding * 2;

  autoTable(doc, {
    startY: toY,
    head: [
      [
        "S.No",
        "Description",
        "Rate",
        "Qty",
        "Gross Amt",
        "Tax Val",
        "GST(%)",
        "GST Amt",
        "Total",
      ],
    ],
    body: productBody,
    margin: {
      left: padding,
      right: padding,
      bottom: pageH - HALF_PAGE_HEIGHT + padding,
    },
    tableWidth: commonTableWidth, // ✅ same as summary
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      fontSize: 6.5,
      cellPadding: { top: 1, right: 1, bottom: 1, left: 1 },
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      valign: "middle",
    },
    headStyles: {
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      font: "Inter-Bold",
      fontStyle: "bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: commonTableWidth * 0.06, halign: "center" }, // S.No (6%)
      1: { cellWidth:"auto", halign: "left" }, // 🟢 Description (30%)
      2: { cellWidth: commonTableWidth * 0.10, halign: "right" }, // Rate (10%)
      3: { cellWidth: commonTableWidth * 0.07, halign: "center" }, // Qty (7%)
      4: { cellWidth: commonTableWidth * 0.10, halign: "right" }, // Gross (10%)
      5: { cellWidth: commonTableWidth * 0.10, halign: "right" }, // Taxable (11%)
      6: { cellWidth: commonTableWidth * 0.07, halign: "center" }, // GST (8%)
      7: { cellWidth: commonTableWidth * 0.10, halign: "right" },
      8: { cellWidth: commonTableWidth * 0.10, halign: "right" }, // Total (8%)
    },

    theme: "grid",
  });

  const amountRowSpan =
    documentConfig?.bankDetails 
      ? isInterState
        ? 5
        : 6
      : isInterState
        ? 3
        : 4;
  const body = [
    [
      {
        content: "Total Amount In Words",
        styles: {
          fontStyle: "bold",
          font: "Inter-Bold",
          fontSize: 6.5,
          valign: "top",
        },
      },
      {
        content: "Total Amount Before Tax",
        styles: { halign: "right", fontSize: 6 },
      },
      {
        content: `${documentConfig?.totalBasePrice}`,
        styles: { halign: "right", valign: "middle", fontSize: 6 },
      },
    ],
    [
      {
        content: documentConfig?.amountInWords || " ",
        rowSpan: amountRowSpan,
        styles: {
          halign: "center",
          valign:
            documentConfig?.bankDetails
              ? "top"
              : "middle",
          fontSize: 6,
        },
      },
      {
        content: isInterState ? "IGST" : "SGST",
        styles: { halign: "right", fontSize: 6 },
      },
      {
        content: `${isInterState ? documentConfig?.igstAmount : documentConfig?.sgstAmount}`,
        styles: { halign: "right", valign: "middle", fontSize: 6 },
      },
    ],
  ];
  if (!isInterState) {
    body.push([
      { content: "CGST", styles: { halign: "right", fontSize: 6 } },
      {
        content: `${documentConfig?.cgstAmount}`,
        styles: { halign: "right", valign: "middle", fontSize: 6 },
      },
    ]);
  }
  // Add Total row
  body.push([
    { content: "Total", styles: { halign: "right", fontSize: 6 } },
    {
      content: `${documentConfig?.totalSoldPrice}`,
      styles: { halign: "right", valign: "middle", fontSize: 6 },
    },
  ]);
  // Add Grand Total row
  body.push([
    {
      content: "Grand Total (Rounded)",
      styles: { fontStyle: "bold", halign: "right", fontSize: 6 },
    },
    {
      content: ` ${documentConfig?.grandTotal}`,
      styles: {
        halign: "right",
        valign: "middle",
        fontStyle: "bold",
        fontSize: 6,
      },
    },
  ]);
  const afterProductsY = doc.lastAutoTable?.finalY || toY;
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginBottom = 20;
  const estimatedSummaryHeight = 50;
  let summaryStartY;
  let isSamePage = true;
  if (
    afterProductsY + estimatedSummaryHeight + marginBottom <=
    HALF_PAGE_HEIGHT
  ) {
    summaryStartY = afterProductsY;
  } else {
    doc.addPage();
    summaryStartY = 5;
    isSamePage = false;
  }
  autoTable(doc, {
    startY: summaryStartY,
    body: body,
    pageBreak: "avoid",
    margin:padding,
    tableWidth: commonTableWidth, // ✅ same width as products
    styles: {
      font: "Inter-Regular",
      fontStyle: "normal",
      halign: "middle",
      cellPadding: 1,
      fontSize: 6,
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
    },
    columnStyles: {
      0: { cellWidth: "auto"}, // left block (Amount in Words)
      1: { cellWidth: commonTableWidth * 0.44, halign: "right" }, // reduced width
      2: { cellWidth: commonTableWidth * 0.10, halign: "right" }, // slightly wider for values
    },

    didParseCell: function (data) {
      const lastRowIndex = body.length - 1;
      if (data?.section === "body") {
        if (
          data?.row.index === lastRowIndex &&
          (data.column.index === 1 || data.column.index === 2)
        ) {
          // data.cell.styles.fillColor = "#f4f4f6";
          // data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.font = "Inter-Bold";
          data.cell.styles.fontStyle = "bold";
        } else {
          data.cell.styles.fillColor = "#ffffff";
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.font = "Inter-Regular";
          data.cell.styles.fontStyle = "normal";
        }
      }
    },
    theme: "grid",
  });

  if (
    documentConfig?.bankDetails 
  ) {
    const table = doc.lastAutoTable;

    // Left cell position
    const leftX = table.settings.margin.left + 2;
    const leftY = table.body[0].cells[0].y;
    const bankY = leftY + 9.5; // Adjusted starting position slightly up

    const leftWidth = table.body[0].cells[0].width;

    let y = bankY + 4;

    doc.setLineWidth(0.5); // Thinner line matches autoTable grid style better
    doc.line(leftX, y, leftX + leftWidth - 4, y);

    y += 3;
    doc.setFont("Inter-Bold", "bold");
    doc.text("Bank Details", leftX, y);

     const bankInfo =[
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],      
    ].filter(([_, value]) => Boolean(value));

    y += 3;
    bankInfo.forEach(([label, value]) => {
      doc.setFont("Inter-Regular", "normal");
      doc.text(label, leftX, y);
      doc.text(String(value || ""), leftX + 22, y); // Reduced x-offset from 20 to 16
      y += 2.8; // Compact spacing to avoid overflow
    });

    const upiId = storeSettings?.paymentSettings?.upiId;
    const beneficiaryName =
      storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";

    if (upiId) {
      const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
        beneficiaryName,
      )}&cu=INR`;

      qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: "L",
        margin: 1,
        scale: 4,
      });

      doc.addImage(
        qrCodeDataUrl,
        "PNG",
        leftX + leftWidth - 28 + 5,
        bankY + 5,
        15,
        15,
      );
    }
  }
  const afterSummaryY = doc.lastAutoTable.finalY;
  const pageHeight2 = doc.internal.pageSize.getHeight();
  const marginBottom2 = 20;
  const declarationHeight = 45;
  let finalY;
  if (afterSummaryY + declarationHeight + marginBottom2 > HALF_PAGE_HEIGHT) {
    doc.addPage();
    finalY = 5;
  } else {
    finalY = afterSummaryY;
  }
  // ---------------- REMARKS ----------------
  if (documentConfig?.remarks?.trim()) {
    const remarkBoxY = finalY;
    const remarkBoxHeight = 14;

    doc.setDrawColor(
      selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    );
    doc.setLineWidth(0.2);

    // Full width border

    // Title
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text("Remark", padding, remarkBoxY + 5);

    // Remark text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const remarks = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      pageWidth - padding * 2 - 4,
    );

    doc.text(remarks, padding, remarkBoxY + 9);

    finalY = remarkBoxY + remarkBoxHeight;
  }
  currentY = finalY + 2;

  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    currentY += 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7);
    doc.text(
      `Your Total Discount : ${documentConfig?.totalDiscount}`,
      pageWidth / 2,
      currentY,
      { align: "center" },
    );

    currentY += 2; // ONLY if discount exists
  }

  const paymentRows = [];


  // condition based rows
  if (parseAmount(documentConfig?.cashPaid) > 0) {
    paymentRows.push(["Cash", `${documentConfig?.cashPaid}`]);
  }

  if (parseAmount(documentConfig?.cardPaid) > 0) {
    paymentRows.push(["Card", `${documentConfig?.cardPaid}`]);
  }

  if (parseAmount(documentConfig?.onlinePaid) > 0) {
    paymentRows.push(["Online Amount", `${documentConfig?.onlinePaid}`]);
  }

  if (parseAmount(documentConfig?.upiPaid) > 0) {
    paymentRows.push(["UPI", `${documentConfig?.upiPaid}`]);
  }

  if (parseAmount(documentConfig?.balanceDue) > 0) {
    paymentRows.push(["Balance Due", `${documentConfig?.balanceDue}`]);
  }
  //  if (paymentRows?.length > 0) {
  //    autoTable(doc, {
  //      startY: currentY,
  //      head: [["Payment Mode", ""]],
  //      body: paymentRows,
  //      margin: { left: 6 },
  //      tableWidth: 70,
  //      styles: {
  //        font: "Inter-Regular",
  //        fontSize: 8,
  //        cellPadding: 2,
  //        lineColor: selectedTheme?.templateTheme?.borderColor,
  //        lineWidth: 0.2,
  //        textColor: "#000",
  //      },
  //      headStyles: {
  //        fillColor: selectedTheme?.templateTheme?.primaryColor,
  //        textColor: selectedTheme?.templateTheme?.foregroundColor,
  //        font: "Inter-Bold",
  //        fontStyle: "bold",
  //        halign: "center",
  //      },
  //      columnStyles: {
  //        0: { cellWidth: 40, halign: "left" },
  //        1: { cellWidth: 30, halign: "right" },
  //      },
  //      theme: "grid",
  //    });

  //    currentY = doc.lastAutoTable.finalY + 6;
  //  }

  //  doc.setFont("Inter-Bold", "bold");
  //  doc.setFontSize(10);
  //  doc.text("***** Thank You! Visit Us Again *****", pageWidth / 2, currentY, {
  //    align: "center",
  //  });
  if (storeSettings?.negobillBranding) {
    currentY += 3;
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(6);
    doc.text("Powerd By", pageWidth / 2, currentY, { align: "center" });
    currentY += 3;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(6);
    doc.text("www.NegoBill.com", pageWidth / 2, currentY, {
      align: "center",
    });
  }
  //  currentY += 2;
  //  const terms=storeSettings?.invoiceSettings?.termsAndConditions;
  //  if (terms?.length > 0) {
  //    doc.setFont("Inter-Bold", "bold");
  //    doc.setFontSize(9);
  //    doc.text("Terms & Conditions", 10, currentY);

  //    doc.setFont("Inter-Regular", "normal");
  //    doc.setFontSize(7);
  //    doc.text(terms, 13, currentY + 4);
  //  }
  const rightXs = pageWidth - padding;

  const boxTopY = currentY - padding;
  // const boxTopY = footerY + 7;
  const boxHeight = 20;
  const boxLeftX = padding;
  const boxRightX = pageWidth - padding;
  const boxWidth = boxRightX - boxLeftX;
  const boxMidX = boxLeftX + boxWidth / 2;
  doc.setFontSize(7);
  doc.setFont("Inter-Regular", "normal");
  //  const titleWidths = doc.getTextWidth("Issued By,Signature");
  //  const titleText = String(data?.salefrom?.title || "");
  doc.text("Issued By,Signature", rightXs, boxTopY, { align: "right" });
  //  doc.setFontSize(9);
  //  doc.setFont("Inter-Bold", "bold");
  //  doc.text(titleText, rightXs, boxTopY + 4, { align: "right" });
  const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;
  if (storeSettings?.invoiceSettings?.signature) {
    try {
      // Convert image URL to base64
      const response = await fetch(signimg);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 50;
      const maxHeight = 10;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(signimg);
      const imgX = rightXs - imgWidth + padding;
      doc.addImage(base64, format, imgX, boxTopY + 1, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }
  //    setDownload(false);
  // setPrint(false);
  return doc;
};

export const generateBILL_HA4_1PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const { documentConfig, isPreSale, timezone } = invoiceConfig;
  const padding = 5;
  const headerX = 2;
  const headerY = 2;
  const HALF_PAGE_HEIGHT = 140;

  const imagePath = documentConfig?.image;
  if (documentConfig?.image) {
    try {
      // Convert image URL to base64
      const response = await fetch(imagePath);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 30;
      const maxHeight = 8;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      // Determine format
      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(imagePath);

      // Add image to PDF
      doc.addImage(base64, format, padding, padding, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }

  const invoiceFontSize = 9;
  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(invoiceFontSize);
  // Estimate vertical alignment by adjusting for font size
  const textHeightOffset = invoiceFontSize * 0.55;
  const invoiceTextY = headerY + textHeightOffset;
  const invoiceX = pageWidth - padding;
  const invoiceText = documentConfig?.title || "";
  const invoiceTextWidth = doc.getTextWidth(invoiceText);
  doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
  // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
  if (data?.status === "CANCELLED") {
    const cancelledFontSize = invoiceFontSize * 0.6;
    doc.setFontSize(cancelledFontSize);
    doc.setTextColor(220, 53, 69); // Bootstrap danger red
    doc.setFont("Inter-Bold", "bold");
    // Position CANCELLED right after INVOICE
    const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
    doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
    // Reset styles
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(invoiceFontSize);
  }

  // Prepare font size for labels and values
  doc.setFontSize(7);
  const invoiceNoLabel = documentConfig?.numberLabel;
  const invoiceNoValue = documentConfig?.number;
  const invoiceDateLabel = "Date:";

  const saleDate = data?.saledate || data?.preSaleData?.preSaleUTCDate;
  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const invoiceDateValue = documentConfig?.Date?.date
  // Measure total widths
  const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
  const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
  const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
  const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
  const gap = 5;
  const innerGap = 2;
  const invoiceRowY = 15;
  const totalTextWidth =
    invoiceNoLabelWidth +
    innerGap +
    invoiceNoValueWidth +
    gap +
    invoiceDateLabelWidth +
    innerGap +
    invoiceDateValueWidth;
  const invoiceRowX = pageWidth - padding - totalTextWidth;
  const lineStartX = padding;
  const lineEndX = invoiceRowX - 5;
  const lineY = invoiceRowY;
  // Middle line
  doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
  doc.setLineWidth(2);
  doc.setLineCap("round");
  doc.line(lineStartX, lineY, lineEndX, lineY);

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceNoValue,
    invoiceRowX + invoiceNoLabelWidth + innerGap,
    invoiceRowY,
  );
  const dateX =
    invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

  doc.setFont("Inter-Bold", "bold");
  doc.text(invoiceDateLabel, dateX, invoiceRowY);

  doc.setFont("Inter-Regular", "normal");
  doc.text(
    invoiceDateValue,
    dateX + invoiceDateLabelWidth + innerGap,
    invoiceRowY,
  );
  // --- Invoice From (between logo & title, in 2 lines) ---
  const topY = headerY + padding; // logo top
  const bottomY = invoiceTextY; // invoice title baseline
  const middleY = topY + (bottomY - topY) / 2; // midpoint

  // Line 1: Company + Address
  const line1 = [
    documentConfig?.fromData?.title || "-",
    [
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      //  documentConfig?.fromData?.country,
      documentConfig?.fromData?.pincode
        ? `- ${documentConfig?.fromData.pincode}`
        : "",
       documentConfig?.fromData?.phone
  ? `Phone: ${documentConfig.fromData.phone}${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`
  : documentConfig?.fromData?.additionalPhone
    ? `Phone: ${documentConfig.fromData.additionalPhone}`
    : "",
      documentConfig?.fromData?.email
        ? `Email: ${documentConfig?.fromData.email}`
        : "",
      //  documentConfig?.fromData?.gstnumber ? `GST: ${documentConfig?.fromData.gstnumber}` : "",
    ]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  const fromDetails = [line1];

  const leftX = padding + 35;
  const rightX = pageWidth - padding - invoiceTextWidth - 10;
  const availableWidth = rightX - leftX - 5;
  let currentY = middleY;
  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);
  fromDetails.forEach((line) => {
    const wrappedLine = doc.splitTextToSize(line, availableWidth);
    doc.text(wrappedLine, leftX, currentY, { align: "left" });
    currentY += wrappedLine.length * 4;
  });

  let toY = invoiceRowY + 6;

  const hasSaleToData =
    documentConfig?.toData &&
    (documentConfig?.toData.name ||
      documentConfig?.toData.addressline1 ||
      documentConfig?.toData.addressline2 ||
      documentConfig?.toData.district ||
      documentConfig?.toData.country ||
      documentConfig?.toData.pincode ||
      documentConfig?.toData.phone ||
      documentConfig?.toData.email ||
      documentConfig?.toData.gstnumber ||
      documentConfig?.toData.state ||
      documentConfig?.toData.statecode ||
      documentConfig?.toData.referenceid);
  if (hasSaleToData) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8.5);
    doc.text(documentConfig?.toLabel, padding, toY);

    toY += 3;

    const toDetailsLine = [
      documentConfig?.toData?.name || "-",
      [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.statecode,
        //  documentConfig?.toData?.country,
        documentConfig?.toData?.pincode
          ? `- ${documentConfig?.toData.pincode}`
          : "",
      ]
        .filter(Boolean)
        .join(", "),
      documentConfig?.toData?.phone
        ? `Phone: ${documentConfig?.toData.phone}`
        : "",
      documentConfig?.toData?.email
        ? `Email: ${documentConfig?.toData.email}`
        : "",
      documentConfig?.toData?.gstnumber
        ? `GST: ${documentConfig?.toData.gstnumber}`
        : "",
      documentConfig?.toData?.referenceid
        ? `Reference No: ${documentConfig?.toData.referenceid}`
        : "",
      documentConfig?.toData?.fssai
        ? `FASSI: ${documentConfig?.toData.fssai}`
        : "",
      documentConfig?.toData?.cinnumber
        ? ` CIN: ${documentConfig?.toData.cinnumber}`
        : "",
      documentConfig?.toData?.pannumber
        ? `PAN: ${documentConfig?.toData.pannumber}`
        : "",
    ]
      .filter(Boolean)
      .join(", ");

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);
    const wrappedAddress = doc.splitTextToSize(
      toDetailsLine,
      pageWidth - padding * 2,
    );

    // 2. Draw the wrapped text
    doc.text(wrappedAddress, padding, toY);

    // 3. IMPORTANT: Update toY based on the number of wrapped lines
    // This ensures the table starts BELOW the address
    toY += wrappedAddress.length * 3;
  }

  // 3. Product Table
  const productBody = documentConfig?.items?.map((item, i) => {
    const qty = item.quantity || 1;
    const rate = item.soldprice;
    const total = item.lineTotalSoldPrice;
    const gross = item.lineTotalSoldPrice;
    const taxable = item.lineTotalBasePrice;
    const gstAmt = item.lineTotalTaxAmount;
    const landingCost = item.lineTotalLandingCost;

    const desc = [
      `${item.title}`,
      ...(item.imode === "PRODUCT" && item.note ? [item.note] : []),
      ...(item.imode === "SERVICE" && item.description
        ? [item.description]
        : []),
      ...(item.hsnsac ? [`HSN/SAC : ${item.hsnsac}`] : []),
      ...(item.discount > 0 ? [`MRP     : ${item.mrp}`] : []),
      ...(item.discount > 0 ? [`Discount: - ${item.discount}`] : []),
      //  `GST     : ${item.taxpercentage}%`,
    ].join("\n");

    return [
      i + 1,
      desc,
      `${rate}`,
      `${qty} ${(() => {
        const unitCode = item?.isSecondaryUnit
          ? item?.unitConfig?.secondaryUnitCode
          : item?.unitConfig?.primaryUnitCode;
        return unitCode || "";
      })()}`,
      // `${gross}`,
      // `${taxable}`,
      // `${gstAmt}`,
      `${total}`,
    ];
  });

  // Add total row INSIDE the same table
  productBody.push([
    {
      content: "",
      colSpan: 2,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    {
      content: "Total",
      styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
    },

    {
      content: "",
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
    // {
    //   content: `${documentConfig?.totalSoldPrice}`,
    //   styles: { fontStyle: "bold", font: "Inter-Bold" },
    // },
    // {
    //   content: `${documentConfig?.totalBasePrice}`,
    //   styles: { fontStyle: "bold", font: "Inter-Bold" },
    // },
    // {
    //   content: `${documentConfig?.totalTaxAmount}`,
    //   styles: { fontStyle: "bold", font: "Inter-Bold" },
    // },
    {
      content: `${documentConfig?.grandTotal}`,
      styles: { fontStyle: "bold", font: "Inter-Bold" },
    },
  ]);

  const commonTableWidth = doc.internal.pageSize.getWidth() - padding * 2;

  autoTable(doc, {
    startY: toY,
    head: [["S.No", "Description", "Rate", "Qty", "Total"]],
    body: productBody,
    margin: {
      left: padding,
      right: padding,
      bottom: pageH - HALF_PAGE_HEIGHT + padding,
    },
    tableWidth: commonTableWidth,
    pageBreak: "avoid",
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: { top: 1, right: 1, bottom: 1, left: 1 },
      lineColor: selectedTheme?.templateTheme?.borderColor,
      lineWidth: 0.2,
      valign: "middle",
    },
    headStyles: {
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
      font: "Inter-Bold",
      fontStyle: "bold",
      fontSize: 8,
      halign: "center",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: commonTableWidth * 0.08, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: commonTableWidth * 0.12, halign: "right" },
      3: { cellWidth: commonTableWidth * 0.1, halign: "center" },
      4: { cellWidth: commonTableWidth * 0.12, halign: "right" },
    },
    theme: "grid",
  });

  // ------------------------- TOTAL AMOUNT IN WORDS -------------------------
  const totalY = doc.lastAutoTable.finalY;

  const summaryBody = [
    // Row 1: Grand Total
    [
      {
        content: "Total Amount In Words",
        styles: {
          font: "Inter-Bold",
          fontStyle: "bold",
          fontSize: 8,
          halign: "left",
        },
      },
      {
        content: "Grand Total",
        styles: { halign: "right", fontSize: 8 },
      },
      {
        content: `${documentConfig?.grandTotal}`,
        styles: { halign: "right", fontSize: 7 },
      },
    ],
    // Row 2: Grand Total (Rounded)
    [
      {
        content:
          capitalizeFirstLetter(documentConfig?.amountInWords || "") + " Only",
        rowSpan: 2, // Spans down alongside Rounded Total & Bank Details
        styles: {
          halign: "center",
          valign: "middle",

          fontSize: 8,
        },
      },
      {
        content: "Grand Total (Rounded)",
        styles: {
          halign: "right",
          font: "Inter-Bold",
          fontStyle: "bold",
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          fontSize: 8,
          textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0],
        },
      },
      {
        content: `${documentConfig?.grandTotal}`,
        styles: {
          halign: "right",
          font: "Inter-Bold",
          fontStyle: "bold",
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          fontSize: 7,
          textColor: selectedTheme?.templateTheme?.foregroundColor || [0, 0, 0],
        },
      },
    ],
    // Row 3: Bank Details + QR Code (merged across last 2 columns)
    ...(documentConfig?.bankDetails 
      ? [
          [
            {
              content: "",
              colSpan: 3, // or 2 if your layout requires it
              styles: {
                minCellHeight: 25,
              },
            },
          ],
        ]
      : []),
  ];

  const upiId = storeSettings?.paymentSettings?.upiId;
  const beneficiaryName =
    storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";

  if (
    qrCodeDataUrl
  ) {
    try {
      const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(beneficiaryName)}&cu=INR`;
      qrCodeDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: "L",
        margin: 1,
      });
    } catch (err) {
      console.warn("QR Generation Error:", err);
    }
  }

  autoTable(doc, {
    startY: totalY,
    body: summaryBody,
    pageBreak: "avoid",
    margin: { left: padding, right: padding },
    tableWidth: commonTableWidth,
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: 1,
      lineColor: selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: {  cellWidth: commonTableWidth * 0.22},
      2: { cellWidth: commonTableWidth * 0.12 },
    },
    didDrawCell: async function (dataCell) {
      // Render Bank Details inside the bottom-right merged cell
      if (
        dataCell.section === "body" &&
        dataCell.row.index === 2 &&
        dataCell.column.index === 1
      ) {
        if (
          documentConfig?.bankDetails
        ) {
          const cell = dataCell.cell;
          const startX = cell.x + 2;
          let startY = cell.y + 4;

          // Title
          doc.setFont("Inter-Bold", "bold");
          doc.setFontSize(8);
          doc.text("Bank Details", startX, startY);

          const bankInfo =[
      ["Account #:", documentConfig?.bankDetails?.accountNo || ""],
      ["IFSC:", documentConfig?.bankDetails?.ifsc || ""],
      ["Name:", documentConfig?.bankDetails?.beneficiaryName || ""],
      ["Bank Name:", documentConfig?.bankDetails?.bankName || ""],
      ["Branch Name:", documentConfig?.bankDetails?.branch || ""],      
    ].filter(([_, value]) => Boolean(value));

          startY += 4;
          bankInfo.forEach(([label, value]) => {
            doc.setFont("Inter-Regular", "normal");
            doc.setFontSize(7);
            doc.text(label, startX, startY);
            doc.text(String(value), startX + 22, startY);
            startY += 3.5;
          });

          // QR Code aligned to right side of the cell
          if (qrCodeDataUrl) {
            doc.addImage(
              qrCodeDataUrl,
              "PNG",
              cell.x + cell.width - 20,
              cell.y + 2,
              16,
              16,
            );
          }
          // const upiId = storeSettings?.paymentSettings?.upiId;
          // const beneficiaryName =
          //   storeSettings?.paymentSettings?.bankDetails?.beneficiaryName || "";

          // if (upiId) {
          //   try {
          //     const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
          //       beneficiaryName
          //     )}&cu=INR`;
          //     const qrCodeDataUrl = await QRCode.toDataURL(upiUrl);

          //     doc.addImage(
          //       qrCodeDataUrl,
          //       "PNG",
          //       cell.x + cell.width - 20,
          //       cell.y + 3,
          //       17,
          //       17
          //     );
          //   } catch (err) {
          //     console.warn(err);
          //   }
          // }
        }
      }
    },
    theme: "grid",
  });
  // ------------------------- FOOTER -------------------------
  const afterSummaryY = doc.lastAutoTable.finalY;

  const pageHeight2 = doc.internal.pageSize.getHeight();
  const marginBottom2 = 20;
  const declarationHeight = 45;
  let finalY;
  if (afterSummaryY + declarationHeight + marginBottom2 > HALF_PAGE_HEIGHT) {
    doc.addPage();
    finalY = 5;
  } else {
    finalY = afterSummaryY;
  }
  if (documentConfig?.remarks?.trim()) {
    const remarkBoxY = finalY;
    const remarkBoxHeight = 14;

    doc.setDrawColor(
      selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    );
    doc.setLineWidth(0.2);

    // Title
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text("Remark", padding, remarkBoxY + 5);

    // Remark text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const remarks = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      pageWidth - padding * 2 - 4,
    );

    doc.text(remarks, padding, remarkBoxY + 9);

    finalY = remarkBoxY + remarkBoxHeight;
  }
  currentY = finalY + 2;

  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    currentY += 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7);
    doc.text(
      `Your Total Discount : ${documentConfig?.totalDiscount}`,
      pageWidth / 2,
      currentY,
      { align: "center" },
    );

    currentY += 2; // ONLY if discount exists
  }

  const paymentRows = [];

  if (parseAmount(documentConfig?.cashPaid) > 0) {
    paymentRows.push(["Cash", `${documentConfig?.cashPaid}`]);
  }

  if (parseAmount(documentConfig?.cardPaid) > 0) {
    paymentRows.push(["Card", `${documentConfig?.cardPaid}`]);
  }

  if (parseAmount(documentConfig?.onlinePaid) > 0) {
    paymentRows.push(["Online Amount", `${documentConfig?.onlinePaid}`]);
  }

  if (parseAmount(documentConfig?.upiPaid) > 0) {
    paymentRows.push(["UPI", `${documentConfig?.upiPaid}`]);
  }

  if (parseAmount(documentConfig?.balanceDue) > 0) {
    paymentRows.push(["Balance Due", `${documentConfig?.balanceDue}`]);
  }

  if (storeSettings?.negobillBranding) {
    currentY += 2;
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(6);
    doc.text("Powerd By", pageWidth / 2, currentY, { align: "center" });
    currentY += 3;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(6);
    doc.text("www.NegoBill.com", pageWidth / 2, currentY, {
      align: "center",
    });
  }
  //  currentY += 2;
  const rightXs = pageWidth - padding;

  const boxTopY = currentY - 2;
  // const boxTopY = footerY + 7;
  const boxHeight = 20;
  const boxLeftX = padding;
  const boxRightX = pageWidth - padding;
  const boxWidth = boxRightX - boxLeftX;
  const boxMidX = boxLeftX + boxWidth / 2;
  doc.setFontSize(7);
  doc.setFont("Inter-Regular", "normal");
  //  const titleWidths = doc.getTextWidth("Issued By,Signature");
  //  const titleText = String(data?.salefrom?.title || "");
  doc.text("Issued By,Signature", rightXs, boxTopY, { align: "right" });
  const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;

  if (signimg) {
    try {
      // Convert image URL to base64
      const response = await fetch(signimg);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Load original dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Resize logic
      const maxWidth = 50;
      const maxHeight = 10;
      let imgWidth = naturalWidth;
      let imgHeight = naturalHeight;
      const widthRatio = maxWidth / imgWidth;
      const heightRatio = maxHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);
      imgWidth *= ratio;
      imgHeight *= ratio;

      const getImageFormat = (path) => {
        const ext = path.split(".").pop().toLowerCase();
        if (ext === "jpg" || ext === "jpeg") return "JPEG";
        if (ext === "png") return "PNG";
        return "JPEG";
      };
      const format = getImageFormat(signimg);
      const imgX = rightXs - imgWidth + padding;
      doc.addImage(base64, format, imgX, boxTopY + 1, imgWidth, imgHeight);
    } catch (err) {
      console.warn("Image failed to load:", err);
    }
  }
  // doc.setFont("Inter-Bold", "bold");
  // doc.setFontSize(9);
  // doc.text("www.NegoBill.com", pageWidth / 2, footerY, { align: "center" });

  // setPrint(false);
  return doc;
};

const RetailInvoice = ({
  data,
  qrCodeDataUrl,
  selectedTheme,  
  invoiceConfig,
}) => {
  const { documentConfig, showTaxDetails, showBillDetails, isInterState, } =
    invoiceConfig;
    const { storeSettings } = usePermission();
  return (
    <>
      {showTaxDetails ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="80%" style={{ textAlign: "left" }}>
                        <div className="d-flex gap-3 align-items-start">
                          <div className="tm_logo">
                            {
                              documentConfig?.image && (
                              <img                                
                                src={documentConfig?.image}
                                className="img-rounded logo mb-0"
                                height="30"
                              />
                            )}
                          </div>
                          {documentConfig?.fromData && (
                            <div
                              style={{ fontSize: "12px", lineHeight: "16px" }}
                            >
                              <div style={{ fontWeight: "600" }}>
                                {documentConfig?.fromData?.title || ""}{" "}
                                {documentConfig?.fromData?.addressline1 || ""}{" "}
                                {documentConfig?.fromData?.addressline2 || ""}{" "}
                                {documentConfig?.fromData?.district || ""}{" "}
                                {documentConfig?.fromData?.country || ""} -{" "}
                                {documentConfig?.fromData?.pincode || ""}
                              </div>

                              <div>
                                Phone:  {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}{" "} |
                                Email: {documentConfig?.fromData?.email}
                              </div>

                              {documentConfig?.fromData?.gstnumber && (
                                <div>
                                  GST: {documentConfig?.fromData?.gstnumber}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td width="20%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color  font-18 fw-bold tm_text_uppercase">
                          {documentConfig.title && <>{documentConfig.title}</>}
                        </div>
                        {data?.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {data?.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">
                        {" "}
                        {documentConfig?.numberLabel}{" "}
                      </b>{" "}
                      {documentConfig?.number}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {documentConfig?.Date?.date}
                    </p>
                  </div>
                </div>
                {documentConfig?.toData && (
                  <table width="100%" className="pdfcontent">
                    <tbody>
                      <tr>
                        <td width="100%" style={{ verticalAlign: "top" }}>
                          {documentConfig?.toData &&
                            (documentConfig?.toData?.name ||
                              documentConfig?.toData?.addressline1 ||
                              documentConfig?.toData?.addressline2 ||
                              documentConfig?.toData?.state ||
                              documentConfig?.toData?.district ||
                              documentConfig?.toData?.country ||
                              documentConfig?.toData?.pincode ||
                              documentConfig?.toData?.phone ||
                              documentConfig?.toData?.email ||
                              documentConfig?.toData?.gstnumber ||
                              documentConfig?.toData?.statecode ||
                              documentConfig?.toData?.referenceid) && (
                              <div>
                                  <div
                                  style={{
                                    fontSize: "14px",
                                    lineHeight: "22px",
                                    fontWeight: "600",
                                    marginBottom: "0px",
                                  }}
                                >
                                  {documentConfig?.toLabel}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    lineHeight: "18px",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {documentConfig?.toData?.name},{" "}
                                  {documentConfig?.toData?.addressline1}{" "}
                                  {documentConfig?.toData?.addressline2},{" "}
                                  {documentConfig?.toData?.district},{" "}
                                  {documentConfig?.toData?.state},{" "}
                                  {documentConfig?.toData?.country} -{" "}
                                  {documentConfig?.toData?.pincode}
                                  {documentConfig?.toData?.phone && (
                                    <>
                                      {" "}
                                      , Phone: +91{" "}
                                      {documentConfig?.toData?.phone}
                                    </>
                                  )}
                                  {documentConfig?.toData?.email && (
                                    <>
                                      {" "}
                                      , Email: {documentConfig?.toData?.email}
                                    </>
                                  )}
                                  {documentConfig?.toData?.gstnumber && (
                                    <>
                                      {" "}
                                      , GST: {documentConfig?.toData?.gstnumber}
                                    </>
                                  )}
                                  {/* {documentConfig?.toData?.state && (
                                    <>
                                      {" "}
                                      , State: {
                                        documentConfig?.toData?.state
                                      }{" "}
                                      [State Code:{" "}
                                      {documentConfig?.toData?.statecode}]
                                    </>
                                  )} */}
                                  {documentConfig?.toData?.referenceid && (
                                    <>
                                      {" "}
                                      , Reference No:{" "}
                                      {documentConfig?.toData?.referenceid}
                                    </>
                                  )}
                                  {documentConfig?.toData?.fssai && (
                                    <>
                                      {" "}
                                      ,FASSI: {documentConfig?.toData?.fssai}
                                    </>
                                  )}
                                  {documentConfig?.toData?.cinnumber && (
                                    <>
                                      {" "}
                                      ,CIN: {documentConfig?.toData?.cinnumber}
                                    </>
                                  )}
                                  {documentConfig?.toData?.pannumber && (
                                    <>
                                      {" "}
                                      ,PAN: {documentConfig?.toData?.pannumber}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          width: "20px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "240px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Description
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "120px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Rate
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "50px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Qty
                      </th>

                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Gross Amt
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Tax Val
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "30px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        GST(%)
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        GST Amt
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentConfig?.items?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "30%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {product.imode === "SERVICE" && (
                            <div className="ms-0 fsz-10 lh-13 mb-1">
                              {product.description}
                            </div>
                          )}
                          {product.imode === "PRODUCT" && product.note && (
                            <div className="ms-0 fsz-10 mb-1">
                              {product?.note}
                            </div>
                          )}
                          {product.hsnsac && (
                            <div>
                              <span className="fw-medium">
                                {" "}
                                {product.imode === "SERVICE" ? "SAC:" : "HSN:"}
                              </span>{" "}
                              {product.hsnsac}
                            </div>
                          )}
                          {product.discount > 0 && (
                            <>
                              <div>
                                <span className="fw-medium">MRP : </span> 
                                {product.mrp}
                              </div>
                              <div>
                                <span className="fw-medium">Discount : </span> -
                                {product.discount}
                              </div>
                            </>
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.soldprice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.quantity}{" "}
                          {(() => {
                            const unitCode = product?.isSecondaryUnit
                              ? product?.unitConfig?.secondaryUnitCode
                              : product?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>

                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.lineTotalSoldPrice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.lineTotalBasePrice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.taxpercentage}%
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.lineTotalTaxAmount}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.lineTotalSoldPrice}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={2}
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalSoldPrice}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalBasePrice}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalTaxAmount}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="5"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        Total Amount Before Tax
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalBasePrice}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="5"
                        rowSpan="5"
                      >
                        {documentConfig?.amountInWords}
                        {documentConfig?.bankDetails 
                           && (
                            <div className="d-flex justify-content-between border-top p-2">
                              <div style={{ textAlign: "left" }}>
                                <strong>Bank Details</strong>

                                {/* Grid container to align labels and values cleanly */}
                               <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "auto 1fr",
                                    columnGap: "12px",
                                    rowGap: "2px",
                                    marginTop: "4px",
                                  }}
                                >
                                 
                                   {documentConfig?.bankDetails?.accountNo && (<>
                                  <span>Account #:</span>
                                  <span>
                                    {
                                     documentConfig
                                        ?.bankDetails?.accountNo
                                    }
                                  </span>
                                </>
                                   )}
                                   {documentConfig?.bankDetails?.ifsc && (<>
                                  <span>IFSC:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.ifsc
                                    }
                                  </span>
                                  </>)}
                                {documentConfig?.bankDetails?.beneficiaryName && (
                                  <><span>Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.beneficiaryName
                                    }
                                  </span></>
                                  )}
                                   {documentConfig?.bankDetails?.bankName && (
                                  <><span>Bank Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.bankName
                                    }
                                  </span></>
                                  )}
                                   {documentConfig?.bankDetails?.branch && (
                                  <><span>Branch Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.branch
                                    }
                                  </span></>
                                  )}
                                  {documentConfig?.upiId && (
                                    <>
                                      <span>UPI:</span>
                                      <span>
                                        {documentConfig?.upiId}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {qrCodeDataUrl && (
                                <img
                                  src={qrCodeDataUrl}
                                  width={90}
                                  height={90}
                                  alt="QR"
                                  style={{ alignSelf: "center" }}
                                />
                              )}
                            </div>
                          )}
                      </td>
                    </tr>
                    {isInterState ? (
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                          colSpan="3"
                        >
                          IGST
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {documentConfig?.igstAmount}
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                            colSpan="3"
                          >
                            SGST
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {documentConfig?.sgstAmount}
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                            colSpan="3"
                          >
                            CGST
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {documentConfig?.cgstAmount}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        TOTAL
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalSoldPrice}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>
                  </tbody>
                </table>
                {documentConfig?.remarks ? (
                  <div className=" p-0 pt-1">
                    <div className="font-12 mt-2 fw-600 text-black mb-0 ">
                      Remark
                    </div>

                    <div
                      className="font-12 mb-2 "
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {capitalizeFirstLetter(documentConfig?.remarks)}
                    </div>
                  </div>
                ) : null}

                <div
                  className="d-flex justify-content-between align-items-end "
                  style={{ marginTop: "15px", width: "100%" }}
                >
                  <div className="text-center flex-grow-1">
                    {parseAmount(documentConfig?.totalDiscount) > 0 && (
                      <div
                        className="fs-20 fw-bold mb-2"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                          color: "black",
                        }}
                      >
                        Your Total Discount :  {documentConfig?.totalDiscount}
                      </div>
                    )}
                    {storeSettings?.negobillBranding && (
                      <div className="mt-0">
                        <div className="fs-20 fw-bold">Powered By</div>
                        <div className="fs-20 fw-bold">www.NegoBill.com</div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", paddingBottom: "8px" }}>
                    <div className="p-0" style={{ fontSize: "14px" }}>
                      Issued By, Signature
                    </div>
                    {storeSettings?.invoiceSettings?.signature && (
                      <div className="mt-1">
                        <img
                          src={
                            uRLs.IMAGE_URL +
                            storeSettings?.invoiceSettings?.signature
                          }
                          width={150}
                          alt="Signature"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : showBillDetails ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in p-2">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="80%" style={{ textAlign: "left" }}>
                        <div className="d-flex gap-3 align-items-start">
                          <div className="tm_logo">
                            {documentConfig?.image && (
                              <img
                                src={documentConfig?.image}
                                className="img-rounded logo mb-0"
                                height="30"
                              />
                            )}
                          </div>
                          {documentConfig?.fromData && (
                            <div
                              style={{ fontSize: "12px", lineHeight: "16px" }}
                            >
                              <div style={{ fontWeight: "600" }}>
                                {documentConfig?.fromData?.title || ""}{" "}
                                {documentConfig?.fromData?.addressline1 || ""}{" "}
                                {documentConfig?.fromData?.addressline2 || ""}{" "}
                                {documentConfig?.fromData?.district || ""}{" "}
                                {documentConfig?.fromData?.country || ""} -{" "}
                                {documentConfig?.fromData?.pincode || ""}
                              </div>

                              <div>
                                Phone:  {documentConfig?.fromData?.phone || ""}
                            {documentConfig?.fromData?.additionalPhone && (
                              <>, {documentConfig.fromData.additionalPhone}</>
                            )}{" "} |
                                Email: {documentConfig?.fromData?.email}
                              </div>

                              {documentConfig?.fromData?.fassi && (
                                <div>
                                  FSSAI: {documentConfig?.fromData?.fassi}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td width="20%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color  font-18 fw-bold tm_text_uppercase">
                          {documentConfig.title && <>{documentConfig.title}</>}
                        </div>
                        {data?.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {data.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">
                        {documentConfig?.numberLabel}{" "}
                      </b>{" "}
                      {documentConfig?.number}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {documentConfig?.Date?.date}
                    </p>
                  </div>
                </div>
                {documentConfig?.toData && (
                  <table width="100%" className="pdfcontent">
                    <tbody>
                      <tr>
                        <td width="100%" style={{ verticalAlign: "top" }}>
                          {documentConfig?.toData &&
                            (documentConfig?.toData?.name ||
                              documentConfig?.toData?.addressline1 ||
                              documentConfig?.toData?.addressline2 ||
                              documentConfig?.toData?.state ||
                              documentConfig?.toData?.district ||
                              documentConfig?.toData?.country ||
                              documentConfig?.toData?.pincode ||
                              documentConfig?.toData?.phone ||
                              documentConfig?.toData?.email ||
                              documentConfig?.toData?.gstnumber ||
                              documentConfig?.toData?.statecode ||
                              documentConfig?.toData?.referenceid) && (
                              <div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    lineHeight: "22px",
                                    fontWeight: "600",
                                    marginBottom: "0px",
                                  }}
                                >
                                  {documentConfig?.toLabel}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    lineHeight: "18px",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {documentConfig?.toData?.name},{" "}
                                  {documentConfig?.toData?.addressline1}{" "}
                                  {documentConfig?.toData?.addressline2},{" "}
                                  {documentConfig?.toData?.district},{" "}
                                  {documentConfig?.toData?.state},{" "}
                                  {documentConfig?.toData?.country} -{" "}
                                  {documentConfig?.toData?.pincode}
                                  {documentConfig?.toData?.phone && (
                                    <>
                                      {" "}
                                      , Phone: +91{" "}
                                      {documentConfig?.toData?.phone}
                                    </>
                                  )}
                                  {documentConfig?.toData?.email && (
                                    <>
                                      {" "}
                                      , Email: {documentConfig?.toData?.email}
                                    </>
                                  )}
                                  {documentConfig?.toData?.gstnumber && (
                                    <>
                                      {" "}
                                      , GST: {documentConfig?.toData?.gstnumber}
                                    </>
                                  )}
                                  {/* {documentConfig?.toData?.state && (
                                    <>
                                      {" "}
                                      , State: {
                                        documentConfig?.toData?.state
                                      }{" "}
                                      [State Code:{" "}
                                      {documentConfig?.toData?.statecode}]
                                    </>
                                  )} */}
                                  {documentConfig?.toData?.referenceid && (
                                    <>
                                      {" "}
                                      , Reference No:{" "}
                                      {documentConfig?.toData?.referenceid}
                                    </>
                                  )}
                                  {documentConfig?.toData?.fssai && (
                                    <>
                                      {" "}
                                      ,FASSI: {documentConfig?.toData?.fssai}
                                    </>
                                  )}
                                  {documentConfig?.toData?.cinnumber && (
                                    <>
                                      {" "}
                                      ,CIN: {documentConfig?.toData?.cinnumber}
                                    </>
                                  )}
                                  {documentConfig?.toData?.pannumber && (
                                    <>
                                      {" "}
                                      ,PAN: {documentConfig?.toData?.pannumber}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          width: "20px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          // border: `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Description
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Rate 
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Qty
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentConfig?.items?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "45%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {product.imode === "SERVICE" && (
                            <div className="ms-0 fsz-10 lh-13 mb-1">
                              {product.description}
                            </div>
                          )}
                          {product.imode === "PRODUCT" && product.note && (
                            <div className="ms-0 fs-12 text-truncate-description text-muted">
                              {product?.note}
                            </div>
                          )}
                          {product.hsnsac && (
                            <div>
                              <span className="fw-medium">
                                {" "}
                                {product.imode === "SERVICE"
                                  ? "SAC:"
                                  : "HSN:"}{" "}
                              </span>{" "}
                              {product.hsnsac}
                            </div>
                          )}
                          {product.discount > 0 && (
                            <>
                              <div>
                                <span className="fw-medium">MRP : </span> 
                                {product.mrp}
                              </div>
                              <div>
                                <span className="fw-medium">Discount : </span> -
                                {product.discount}
                              </div>
                            </>
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.soldprice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product?.quantity}
                          {(() => {
                            const unitCode = product?.isSecondaryUnit
                              ? product?.unitConfig?.secondaryUnitCode
                              : product?.unitConfig?.primaryUnitCode;
                            return unitCode || "";
                          })()}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.lineTotalSoldPrice}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                        colSpan={2}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {/* {globalData.totalqty} */}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.totalSoldPrice}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                        rowSpan="5"
                      >
                        {documentConfig?.amountInWords}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {documentConfig?.grandTotal}
                      </td>
                    </tr>

                    <tr>
                      {documentConfig?.bankDetails && (
                          <td
                            colSpan="5"
                            className="force-style"
                            style={{
                              padding: "10px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                            }}
                          >
                            <div className="d-flex justify-content-between  p-2">
                              <div style={{ textAlign: "left" }}>
                                <strong>Bank Details</strong>

                                {/* Grid container to align labels and values cleanly */}
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "auto 1fr",
                                    columnGap: "12px",
                                    rowGap: "2px",
                                    marginTop: "4px",
                                  }}
                                >
                                 
                                   {documentConfig?.bankDetails?.accountNo && (<>
                                  <span>Account #:</span>
                                  <span>
                                    {
                                     documentConfig
                                        ?.bankDetails?.accountNo
                                    }
                                  </span>
                                </>
                                   )}
                                   {documentConfig?.bankDetails?.ifsc && (<>
                                  <span>IFSC:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.ifsc
                                    }
                                  </span>
                                  </>)}
                                {documentConfig?.bankDetails?.beneficiaryName && (
                                  <><span>Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.beneficiaryName
                                    }
                                  </span></>
                                  )}
                                   {documentConfig?.bankDetails?.bankName && (
                                  <><span>Bank Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.bankName
                                    }
                                  </span></>
                                  )}
                                   {documentConfig?.bankDetails?.branch && (
                                  <><span>Branch Name:</span>
                                  <span>
                                    {
                                      documentConfig
                                        ?.bankDetails?.branch
                                    }
                                  </span></>
                                  )}
                                  {documentConfig?.upiId && (
                                    <>
                                      <span>UPI:</span>
                                      <span>
                                        {documentConfig?.upiId}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {qrCodeDataUrl && (
                                <img
                                  src={qrCodeDataUrl}
                                  width={90}
                                  height={90}
                                  alt="QR"
                                  style={{ alignSelf: "center" }}
                                />
                              )}
                            </div>
                          </td>
                        )}
                    </tr>
                  </tbody>
                </table>
                {documentConfig?.remarks ? (
                  <div className=" p-0 pt-1">
                    <div className="font-12 mt-2 fw-600 text-black mb-0 ">
                      Remark
                    </div>

                    <div
                      className="font-12 mb-2 "
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {capitalizeFirstLetter(documentConfig?.remarks)}
                    </div>
                  </div>
                ) : null}
                <div
                  className="d-flex justify-content-between align-items-end "
                  style={{ marginTop: "15px", width: "100%" }}
                >
                  <div className="text-center flex-grow-1">
                    {parseAmount(documentConfig?.totalDiscount) > 0 && (
                      <div
                        className="fs-20 fw-bold mb-2"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                          color: "black",
                        }}
                      >
                        Your Total Discount :  {documentConfig?.totalDiscount}
                      </div>
                    )}
                    {storeSettings?.negobillBranding && (
                      <div className="mt-0">
                        <div className="fs-20 fw-bold">Powered By</div>
                        <div className="fs-20 fw-bold">www.NegoBill.com</div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", paddingBottom: "8px" }}>
                    <div className="p-0" style={{ fontSize: "14px" }}>
                      Issued By, Signature
                    </div>
                    {storeSettings?.invoiceSettings?.signature && (
                      <div className="mt-1">
                        <img
                          src={
                            uRLs.IMAGE_URL +
                            storeSettings?.invoiceSettings?.signature
                          }
                          width={150}
                          alt="Signature"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
    </>
  );
};

export default RetailInvoice;
